import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import { Title } from "@angular/platform-browser";
import { firstValueFrom } from "rxjs";
import { EChartsOption } from "echarts/types/dist/shared";
import { toPng } from "html-to-image";

import { SegyService } from "../../../../shared/services/segy.service";
import {
  CustomHeaderField,
  FilterPhase,
  GatherResponse,
  GatherSamples,
  RawHeadersResponse,
  RawTraceHeadersResponse,
  SegyAutomuteResponse,
  SegyDirListing,
  SegyFileInfo,
  SegyGatherHeadersResponse,
  SegyGatherParams,
  SegyGridBox,
  SegyModelGeometryResponse,
  SegyScanResponse,
  SegySpectrumResponse,
  SEGY_HEADER_FORMATS,
  customHeaderFieldName,
  customHeaderFieldToken,
} from "../../../../models/segy";
import {
  buildFieldOptions,
  byteRangeLabel,
} from "../field-select/field-select.component";
import {
  DEFAULT_SPECTRUM_LIMITS,
  SpectrumLimits,
} from "../spectrum-chart/spectrum-chart.component";
import {
  DEFAULT_GRID_BOX,
  GeometryLayers,
} from "../geometry-map/geometry-map.component";
import {
  GatherDisplayMode,
  GatherHeaderTrack,
  GatherMuteLine,
} from "../gather-viewer/gather-viewer.component";
import { GeometryHighlight } from "../geometry-map/geometry-map.component";
import { HeaderTrackSeries } from "../header-tracks/header-tracks.component";

/** SEG-Y binary-header sample format codes (rev1). */
const FORMAT_LABELS: Record<number, string> = {
  1: "IBM float",
  2: "int32",
  3: "int16",
  4: "fixed point + gain",
  5: "IEEE float",
  8: "int8",
};

const TRACE_PAGE_SIZE = 200;
const GATHER_HEADER_PAGE_SIZE = 100;
const DEFAULT_TRACE_COUNT = 500;
const DEFAULT_SCAN_POINTS = 5000;
/** Server-side cap on one header scan (HEADER_SCAN_MAX_TRACES). */
const MAX_SCAN_POINTS = 250000;
const SCAN_PRESETS = [1000, 5000, 20000, 50000, 100000, 250000];

/** Default columns of both trace-header tables. */
const DEFAULT_HEADER_COLUMNS = [
  "ffid",
  "chan",
  "source",
  "offset",
  "cdp",
  "soux",
  "souy",
  "recx",
  "recy",
  "souelev",
  "soudepth",
  "recelev",
  "scalel",
  "scalco",
];

/** Header tracks shown above the gather / over the trace table by default. */
const DEFAULT_TRACK_FIELDS = ["offset"];
const DEFAULT_TABLE_PLOT_FIELDS = ["ffid", "offset"];
/** More stacked tracks than this stop being readable. */
const MAX_TRACKS = 5;

/** Coordinates the gather header fetch always carries, so the displayed
 * gather can be marked on the geometry map and mutes can use src–rcv
 * distance. */
const GEOMETRY_FIELDS = [
  "soux",
  "souy",
  "recx",
  "recy",
  "scalco",
  "souelev",
  "soudepth",
  "recelev",
  "scalel",
];

/** A linear mute, as on the shot-image page / runfile (outer_mute_*,
 * inner_mute_*): t(d) = start + (d − atOffset) / growth, d = source–receiver
 * distance. */
export interface SegyMute {
  name: string;
  on: boolean;
  /** s */
  start: number | null;
  /** m/s */
  growth: number | null;
  /** m */
  atOffset: number | null;
  color: string;
}
type MuteDistance = "offset" | "horizontal" | "3d";

/** Auto mute picking is hidden until the picker is reliable enough; the
 * endpoint and code stay in place. */
const AUTO_PICK_ENABLED = false;

/** ft -> m: mute values are always metres / m/s. */
const FEET_TO_METRES = 0.3048;

/** Auto pick writes its fit into this mute (mute 3). */
const AUTO_MUTE_INDEX = 2;

const DEFAULT_MUTES: SegyMute[] = [
  { name: "mute 1", on: false, start: 0.1, growth: 1500, atOffset: 0, color: "#00e676" },
  { name: "mute 2", on: false, start: 1.5, growth: 1500, atOffset: 0, color: "#ffd600" },
  { name: "mute 3", on: false, start: 1, growth: 2500, atOffset: 0, color: "#d500f9" },
];

/** Tab order in the template; used to lazy-load the header scans. */
const TAB = { display: 0, geometry: 1, headers: 2, model: 3 } as const;

const SETTINGS_KEY = "segy-viewer-settings-v2";
const BOX_KEY_PREFIX = "segy-viewer-box:";
/** Files opened in this browser, newest first (shared by every tab). */
const RECENT_KEY = "segy-viewer-recent";
const MAX_RECENT = 15;

interface RecentFile {
  path: string;
  isAws: boolean;
  size: number | null;
  openedAt: number;
}

function readRecent(): RecentFile[] {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
    return Array.isArray(list)
      ? list.filter((r) => typeof r?.path === "string")
      : [];
  } catch {
    return [];
  }
}

type GatherMode = "field" | "range";
type ClipMode = "percentile" | "fixed";

/** Display / plot choices remembered between visits (per browser), so the
 * same gather framing, filter and spectrum limits can be reproduced. */
interface ViewerSettings {
  gatherMode: GatherMode;
  scanField: string;
  traceCount: number;
  clipMode: ClipMode;
  clip: number;
  ampLimit: number | null;
  tMin: number | null;
  tMax: number | null;
  lowcut: number | null;
  highcut: number | null;
  lowRoll: number;
  highRoll: number;
  lowPhase: FilterPhase;
  highPhase: FilterPhase;
  spectrumLimits: SpectrumLimits;
  gatherColumns: string[];
  tableColumns: string[];
  trackFields: string[];
  showSpectrum: boolean;
  screenshotTracks: boolean;
  showMutes: boolean;
  displayMode: GatherDisplayMode;
  wiggleGain: number;
  wiggleStep: number | null;
  mutes: SegyMute[];
  muteDistance: MuteDistance;
  showGatherOnMap: boolean;
  boxFitAngle: boolean;
  boxPadding: number;
  autoShiftMs: number;
  showAutoPicks: boolean;
  tablePlotFields: string[];
  scanPoints: number;
  applyScalars: boolean;
  geoColor: string | null;
  geoLayers: GeometryLayers;
  xField: string;
  yField: string;
  xplotColor: string | null;
  customFields: CustomHeaderField[];
}

function readStorage<T>(key: string): Partial<T> {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Partial<T>) : {};
  } catch {
    return {};
  }
}

function writeStorage(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage unavailable (private mode / quota): settings just aren't kept
  }
}

/**
 * Standalone viewer for a single *raw* SEG-Y file addressed directly by path —
 * no registered prep run required. Tabs:
 *
 *  - Display: a gather (by any header value or a trace range) with fixed
 *    display limits (clip, time window), a bandpass filter, its spectrum, and
 *    the header values of every trace in it.
 *  - Geometry & header plots: one sampled header scan (sampling chosen by the
 *    user) feeding the source/receiver map — coloured by any header, with a
 *    model grid box overlay — and the header cross-plots.
 *  - Headers: textual + binary headers and the paged file-wide trace table.
 *  - Model: survey geometry (origin, rotation, bins) of a 3D volume.
 *
 * Every header choice uses the same field picker, built from one catalog
 * (standard rev1 fields + the user's custom byte/format fields).
 */
@Component({
  selector: "app-segy-viewer",
  templateUrl: "./segy-viewer.component.html",
  styleUrl: "./segy-viewer.component.less",
  standalone: false,
})
export class SegyViewerComponent {
  private segy = inject(SegyService);
  private title = inject(Title);

  constructor() {
    // name the browser tab after the open file, so tabs with different
    // datasets can be told apart when flipping between them
    const pageTitle = this.title.getTitle();
    effect(() => {
      const info = this.info();
      this.title.setTitle(
        info ? `${this.fileName(info.path)} · SEG-Y` : pageTitle,
      );
    });
    inject(DestroyRef).onDestroy(() => this.title.setTitle(pageTitle));

    // Preload the browsable bucket list so the directory browser can offer
    // them as top-level roots without the user typing a bucket name.
    this.loadBuckets();
    this.restoreSettings();

    // remember display / plot choices
    effect(() => writeStorage(SETTINGS_KEY, this.settings()));
    // remember the grid box per file
    effect(() => {
      const key = this.boxStorageKey();
      const box = this.gridBox();
      if (key) writeStorage(key, box);
    });
    // a plot asked for a field the current scan doesn't carry: rescan with it
    effect(() => {
      const missing = this.missingScanFields();
      if (!missing.length || !this.scan()) return;
      untracked(() => {
        if (this.scanLoading()) return;
        const key = missing.join(",");
        if (key === this.lastMissingRequest) return; // don't retry a failure
        this.lastMissingRequest = key;
        this.loadScan();
      });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // INPUT FORM
  // ─────────────────────────────────────────────────────────────

  /** "file" = open a single file by path; "directory" = browse a folder and
   * pick a file from it (a free lookup, no registration). */
  protected readonly inputMode = signal<"file" | "directory">("directory");

  protected readonly pathInput = signal<string>("");
  /** true = S3 (`bucket/key`), false = local (under SUBSTRATA_LOCAL_PATH). */
  protected readonly isAws = signal<boolean>(true);

  // ── Directory lookup ──
  /** The directory typed into the lookup (what gets listed on "List"). */
  protected readonly dirInput = signal<string>("");
  protected readonly listing = signal<SegyDirListing | null>(null);
  protected readonly listLoading = signal<boolean>(false);
  protected readonly listError = signal<string | null>(null);

  /** Parent of the currently-listed directory, or null at the top. */
  protected readonly parentDir = computed<string | null>(() => {
    const current = this.listing()?.path;
    if (!current) return null;
    const trimmed = current.replace(/\/+$/, "");
    const cut = trimmed.lastIndexOf("/");
    return cut > 0 ? trimmed.slice(0, cut) : null;
  });

  /** Clickable segments of the listed path (bucket first on S3). */
  protected readonly dirCrumbs = computed<{ name: string; path: string }[]>(
    () => {
      const parts = (this.listing()?.path ?? "").split("/").filter(Boolean);
      return parts.map((name, i) => ({
        name,
        path: parts.slice(0, i + 1).join("/"),
      }));
    },
  );

  protected readonly dirPlaceholder = computed(() =>
    this.isAws()
      ? "bucket/path/to/segyprep-run"
      : "relative/or/absolute/path/to/directory",
  );

  /** The file currently loaded (fixed on "Load"); drives every fetch. */
  protected readonly loaded = signal<{ path: string; isAws: boolean } | null>(
    null,
  );

  protected readonly placeholder = computed(() =>
    this.isAws()
      ? "bucket/path/to/file.sgy"
      : "relative/or/absolute/path/to/file.sgy",
  );

  // ─────────────────────────────────────────────────────────────
  // FILE INFO + HEADER FIELD CATALOG
  // ─────────────────────────────────────────────────────────────

  protected readonly info = signal<SegyFileInfo | null>(null);
  protected readonly infoLoading = signal<boolean>(false);
  protected readonly loadError = signal<string | null>(null);

  // ── Custom byte/format fields (available in every picker) ──
  protected readonly headerFormats = SEGY_HEADER_FORMATS;
  protected readonly customFields = signal<CustomHeaderField[]>([]);
  protected readonly customByteDraft = signal<number | null>(null);
  protected readonly customFormatDraft = signal<string>("int32");
  protected readonly customError = signal<string | null>(null);
  private readonly customFieldNames = computed<string[]>(() =>
    this.customFields().map(customHeaderFieldName),
  );

  /** The one option list behind every header picker on the page. */
  protected readonly fieldOptions = computed(() =>
    buildFieldOptions(this.info()?.header_fields ?? [], this.customFields()),
  );
  /** Field name -> byte range, for table column headers. */
  private readonly fieldBytes = computed(() => {
    const map: { [name: string]: [number, number] } = {};
    for (const o of this.fieldOptions()) if (o.bytes) map[o.value] = o.bytes;
    return map;
  });
  /** Field name -> picker label (custom fields read "byte 197 · int32"). */
  private readonly fieldLabels = computed(() => {
    const map = new Map<string, string>();
    for (const o of this.fieldOptions()) map.set(o.value, o.label);
    return map;
  });

  // ─────────────────────────────────────────────────────────────
  // HEADERS (textual + binary)
  // ─────────────────────────────────────────────────────────────

  protected readonly headers = signal<RawHeadersResponse | null>(null);
  protected readonly headersLoading = signal<boolean>(false);
  protected readonly headersError = signal<string | null>(null);

  protected readonly binaryHeaderEntries = computed<
    { field: string; value: number }[]
  >(() => {
    const header = this.headers()?.binary_header;
    if (!header) return [];
    return Object.entries(header).map(([field, value]) => ({ field, value }));
  });

  // ─────────────────────────────────────────────────────────────
  // FILE-WIDE TRACE HEADER TABLE (server-paged)
  // ─────────────────────────────────────────────────────────────

  protected readonly traceHeaders = signal<RawTraceHeadersResponse | null>(
    null,
  );
  protected readonly traceHeadersLoading = signal<boolean>(false);
  protected readonly traceHeadersError = signal<string | null>(null);
  /** 1-based page index for nz-table. */
  protected readonly tracePage = signal<number>(1);
  protected readonly tracePageSize = TRACE_PAGE_SIZE;
  /** The columns shown in the file-wide table. */
  protected readonly tableColumns = signal<string[]>([
    ...DEFAULT_HEADER_COLUMNS,
  ]);

  protected readonly traceTable = computed(() => {
    const res = this.traceHeaders();
    if (!res) return null;
    const cols = this.tableColumns().filter((c) => res.columns.includes(c));
    const idx = cols.map((c) => res.columns.indexOf(c));
    return {
      columns: this.columnInfo(cols),
      rows: res.rows.map((row, i) => ({
        trace: res.offset + i + 1,
        values: idx.map((j) => row[j]),
      })),
      total: res.total_traces,
    };
  });

  /** Fields plotted against the trace number for the page in the table. */
  protected readonly tablePlotFields = signal<string[]>([
    ...DEFAULT_TABLE_PLOT_FIELDS,
  ]);
  protected readonly tablePlot = computed<{
    traces: number[];
    series: HeaderTrackSeries[];
  } | null>(() => {
    const res = this.traceHeaders();
    if (!res) return null;
    const series = this.tablePlotFields()
      .filter((f) => res.columns.includes(f))
      .map((name) => {
        const j = res.columns.indexOf(name);
        return {
          name,
          label: this.fieldLabel(name),
          values: res.rows.map((row) => row[j]),
        };
      });
    return {
      traces: res.rows.map((_, i) => res.offset + i + 1),
      series,
    };
  });
  /** First / last trace number of the table page (for the plot caption). */
  protected readonly tablePageRange = computed<[number, number] | null>(() => {
    const t = this.tablePlot()?.traces;
    return t?.length ? [t[0], t[t.length - 1]] : null;
  });

  // ─────────────────────────────────────────────────────────────
  // DISPLAY: gather addressing
  // ─────────────────────────────────────────────────────────────

  protected readonly gatherMode = signal<GatherMode>("field");
  protected readonly scanField = signal<string>("ffid");
  protected readonly scanValue = signal<number | null>(null);
  protected readonly traceStart = signal<number>(0);
  protected readonly traceCount = signal<number>(DEFAULT_TRACE_COUNT);

  // ── Display limits (kept between gathers so views are comparable) ──
  protected readonly clipMode = signal<ClipMode>("percentile");
  protected readonly clip = signal<number>(98);
  /** fixed symmetric amplitude limit (±) when clipMode = "fixed" */
  protected readonly ampLimit = signal<number | null>(null);
  /** time window (s); null = start / end of the record */
  protected readonly tMin = signal<number | null>(null);
  protected readonly tMax = signal<number | null>(null);

  /** Record length (s) of the file. */
  protected readonly totalTime = computed<number | null>(
    () => this.info()?.total_time ?? null,
  );

  // ── Bandpass filter (applied to both the gather and its spectrum) ──
  // Slopes are in dB/octave (low_roll/high_roll), matching the pyprep .toml.
  protected readonly lowcut = signal<number | null>(null);
  protected readonly highcut = signal<number | null>(null);
  protected readonly lowRoll = signal<number>(48);
  protected readonly highRoll = signal<number>(48);
  protected readonly rolls = [24, 48, 72, 96];
  /** Per-leg phase mode: zero-phase (forward-backward, no shift) or minimum
   * -phase (single causal pass), matching the run-based filter panel. */
  protected readonly lowPhase = signal<FilterPhase>("zero");
  protected readonly highPhase = signal<FilterPhase>("zero");
  protected readonly phases: { value: FilterPhase; label: string }[] = [
    { value: "zero", label: "Zero" },
    { value: "min", label: "Min" },
  ];
  protected readonly hasFilter = computed<boolean>(
    () => this.lowcut() != null || this.highcut() != null,
  );
  /** Nyquist (Hz) from the file dt; upper bound for the cut controls. */
  protected readonly nyquist = computed<number>(() => {
    const dt = this.info()?.dt;
    if (!dt || dt <= 0) return 0;
    return Math.floor((0.5 / dt) * 2) / 2;
  });
  /** Upper bound (Hz) for the bandpass cut inputs. */
  protected readonly maxCut = computed<number>(() => this.nyquist() || Infinity);

  // ── Gather result ──
  // When a bandpass filter is active both the raw and filtered renders are
  // fetched and cached, so the displayed gather can be flipped between them
  // instantly to see the filter's impact.
  protected readonly rawGather = signal<GatherResponse | null>(null);
  protected readonly filteredGather = signal<GatherResponse | null>(null);
  protected readonly gatherLoading = signal<boolean>(false);
  protected readonly gatherError = signal<string | null>(null);
  /** Flip the displayed gather to the unfiltered (raw) render. */
  protected readonly showRaw = signal<boolean>(false);

  protected readonly hasFilteredGather = computed<boolean>(
    () => this.filteredGather() != null,
  );

  /** The gather currently shown: filtered by default, raw when flipped — and
   * whichever exists when only one does. */
  protected readonly gather = computed<GatherResponse | null>(() => {
    const filtered = this.filteredGather();
    const raw = this.rawGather();
    return this.showRaw() ? (raw ?? filtered) : (filtered ?? raw);
  });

  // ── Cells / wiggle display: the samples, drawn client-side ──
  protected readonly displayMode = signal<GatherDisplayMode>("image");
  protected readonly wiggleGain = signal<number>(1);
  /** Draw every n-th trace as a wiggle; null = auto (from the zoom). */
  protected readonly wiggleStep = signal<number | null>(null);
  /** The step the viewer picked in auto mode, shown as the placeholder. */
  protected readonly wiggleAutoStride = signal<number>(1);
  protected readonly rawSamples = signal<GatherSamples | null>(null);
  protected readonly filteredSamples = signal<GatherSamples | null>(null);
  protected readonly samplesLoading = signal<boolean>(false);
  /** The request behind the current render, so samples can be fetched for
   * it later (switching display mode) without picking up control edits. */
  private renderedRequest: {
    path: string;
    isAws: boolean;
    params: SegyGatherParams;
    filter: SegyGatherParams | null;
  } | null = null;
  /** Bumped per render so a late samples response for an older gather is
   * dropped. */
  private samplesToken = 0;

  /** Samples matching the displayed (raw / filtered) gather. */
  protected readonly samples = computed<GatherSamples | null>(() => {
    const filtered = this.filteredSamples();
    const raw = this.rawSamples();
    return this.showRaw() ? (raw ?? filtered) : (filtered ?? raw);
  });

  protected setDisplayMode(mode: GatherDisplayMode) {
    this.displayMode.set(mode);
    if (mode !== "image" && !this.rawSamples() && !this.samplesLoading()) {
      this.fetchSamples();
    }
  }

  /** Fetch the samples of the rendered gather (raw, and filtered when the
   * render had a filter) for the cells / wiggle display. */
  private async fetchSamples() {
    const req = this.renderedRequest;
    if (!req) return;
    const token = ++this.samplesToken;
    this.samplesLoading.set(true);
    const [raw, filt] = await Promise.allSettled([
      firstValueFrom(this.segy.getGatherSamples(req.path, req.isAws, req.params)),
      req.filter
        ? firstValueFrom(
            this.segy.getGatherSamples(req.path, req.isAws, {
              ...req.params,
              ...req.filter,
            }),
          )
        : Promise.resolve<GatherSamples | null>(null),
    ]);
    if (token !== this.samplesToken) return;
    this.rawSamples.set(raw.status === "fulfilled" ? raw.value : null);
    this.filteredSamples.set(filt.status === "fulfilled" ? filt.value : null);
    if (raw.status === "rejected" && !this.gatherError()) {
      this.gatherError.set(
        "Could not load the gather samples for the cells / wiggle display.",
      );
    }
    this.samplesLoading.set(false);
  }

  private clearSamples() {
    this.samplesToken++;
    this.renderedRequest = null;
    this.rawSamples.set(null);
    this.filteredSamples.set(null);
    this.samplesLoading.set(false);
  }

  protected readonly canViewGather = computed<boolean>(() => {
    if (this.gatherLoading()) return false;
    if (this.gatherMode() === "field") return this.scanValue() != null;
    return this.traceStart() != null && (this.traceCount() ?? 0) > 0;
  });

  /** One-line description of what the displayed gather is. */
  protected readonly gatherSummary = computed<string | null>(() => {
    const g = this.gather();
    const a = this.renderedAddressing();
    if (!g || !a) return null;
    const meta = g.meta as any;
    const what =
      a.scan_field != null
        ? `${this.fieldLabel(a.scan_field)} = ${a.scan_value}`
        : `traces from index ${a.trace_start}`;
    const from =
      meta.trace_start != null ? ` · from file trace ${meta.trace_start + 1}` : "";
    return `${what} · ${g.meta.n_traces} traces${from}`;
  });

  // ── Spectrum of the displayed gather (raw + filtered) ──
  protected readonly spectrum = signal<SegySpectrumResponse | null>(null);
  protected readonly spectrumError = signal<string | null>(null);
  protected readonly spectrumLimits = signal<SpectrumLimits>({
    ...DEFAULT_SPECTRUM_LIMITS,
  });

  // ── Header values of every trace in the displayed gather ──
  protected readonly gatherColumns = signal<string[]>([
    ...DEFAULT_HEADER_COLUMNS,
  ]);
  protected readonly gatherHeaders = signal<SegyGatherHeadersResponse | null>(
    null,
  );
  protected readonly gatherHeadersLoading = signal<boolean>(false);
  protected readonly gatherHeadersError = signal<string | null>(null);
  protected readonly gatherHeaderPage = signal<number>(1);
  protected readonly gatherHeaderPageSize = GATHER_HEADER_PAGE_SIZE;

  /** The fetch carries the table columns and the track fields together, so
   * the table picks its own columns out of it. */
  protected readonly gatherTable = computed(() => {
    const res = this.gatherHeaders();
    if (!res) return null;
    const cols = this.gatherColumns().filter((c) => res.columns.includes(c));
    const idx = cols.map((c) => res.columns.indexOf(c));
    return {
      columns: this.columnInfo(cols),
      rows: res.rows.map((row, ordinal) => ({
        ordinal,
        trace: res.trace[ordinal],
        values: idx.map((j) => row[j]),
      })),
    };
  });

  /** Header fields plotted as tracks above the gather. */
  protected readonly trackFields = signal<string[]>([...DEFAULT_TRACK_FIELDS]);
  protected readonly maxTracks = MAX_TRACKS;
  protected readonly gatherTracks = computed<GatherHeaderTrack[]>(() => {
    const res = this.gatherHeaders();
    if (!res) return [];
    return this.trackFields()
      .filter((f) => res.columns.includes(f))
      .map((name) => {
        const j = res.columns.indexOf(name);
        return {
          name,
          label: this.fieldLabel(name),
          values: res.rows.map((row) => row[j]),
        };
      });
  });

  // ── Mutes over the gather (client-side: no refetch needed) ──
  protected readonly showMutes = signal<boolean>(false);
  protected readonly mutes = signal<SegyMute[]>(
    DEFAULT_MUTES.map((m) => ({ ...m })),
  );
  protected readonly muteDistance = signal<MuteDistance>("offset");
  /** Distance units of this file's headers. Set from the binary header's
   * measurement system on load (1 = m, 2 = ft) and switchable; feet are
   * converted so mute values stay in m and m/s. */
  protected readonly fileUnits = signal<"m" | "ft">("m");
  /** What the binary header says, for the switch's hint (null = unset). */
  protected readonly headerUnits = computed<"m" | "ft" | null>(() => {
    const code = this.headers()?.binary_header?.["measurement_system"];
    return code === 2 ? "ft" : code === 1 ? "m" : null;
  });
  protected readonly autoPickEnabled = AUTO_PICK_ENABLED;

  /** Header column of the gather fetch, or null when it isn't there. */
  private gatherColumn(name: string): number[] | null {
    const res = this.gatherHeaders();
    const j = res?.columns.indexOf(name) ?? -1;
    return res && j >= 0 ? res.rows.map((row) => row[j]) : null;
  }

  /** Per-trace multiplier from scalco (SEG-Y: < 0 divides, > 0 multiplies). */
  private coordScale(): number[] | null {
    const scalco = this.gatherColumn("scalco");
    return scalco?.map((s) => (s < 0 ? -1 / s : s > 0 ? s : 1)) ?? null;
  }

  /** Source–receiver distance (m) of every trace in the displayed gather. */
  private readonly gatherDistances = computed<number[] | null>(() => {
    const d = this.gatherDistancesInFileUnits();
    const k = this.fileUnits() === "ft" ? FEET_TO_METRES : 1;
    return d && k !== 1 ? d.map((x) => x * k) : d;
  });

  private readonly gatherDistancesInFileUnits = computed<number[] | null>(() => {
    if (this.muteDistance() === "offset") {
      return this.gatherColumn("offset")?.map(Math.abs) ?? null;
    }
    const [sx, sy, rx, ry] = ["soux", "souy", "recx", "recy"].map((f) =>
      this.gatherColumn(f),
    );
    const k = this.coordScale();
    if (!sx || !sy || !rx || !ry || !k) return null;
    if (this.muteDistance() === "horizontal") {
      return sx.map((_, i) => Math.hypot(sx[i] - rx[i], sy[i] - ry[i]) * k[i]);
    }
    // 3D: source z = souelev − soudepth, receiver z = recelev (× scalel)
    const [se, sd, re, sl] = ["souelev", "soudepth", "recelev", "scalel"].map(
      (f) => this.gatherColumn(f),
    );
    if (!se || !sd || !re || !sl) return null;
    return sx.map((_, i) => {
      const kz = sl[i] < 0 ? -1 / sl[i] : sl[i] > 0 ? sl[i] : 1;
      const dz = (se[i] - sd[i] - re[i]) * kz;
      return Math.hypot((sx[i] - rx[i]) * k[i], (sy[i] - ry[i]) * k[i], dz);
    });
  });

  protected readonly muteOnCount = computed(
    () => this.mutes().filter((m) => m.on).length,
  );

  protected readonly muteLines = computed<GatherMuteLine[]>(() => {
    if (!this.showMutes()) return [];
    const d = this.gatherDistances();
    const lines: GatherMuteLine[] = d
      ? this.mutes()
          .filter((m) => m.on && m.start != null)
          .map((m) => ({
            label: m.name,
            color: m.color,
            // no growth = a flat mute at the start time
            times: d.map((x) =>
              m.growth ? m.start! + (x - (m.atOffset ?? 0)) / m.growth : m.start!,
            ),
          }))
      : [];
    // the picked (per-trace) curve, dashed, beside the fitted line
    const auto = this.automute();
    if (AUTO_PICK_ENABLED && auto && this.showAutoCurves()) {
      const target = this.mutes()[AUTO_MUTE_INDEX];
      lines.push({
        label: "auto picks",
        color: target.color,
        times: auto.top,
        dashed: true,
      });
    }
    return lines;
  });

  // ── Auto top-mute picking (scprep's auto gate top, server-side) ──
  protected readonly automute = signal<SegyAutomuteResponse | null>(null);
  protected readonly automuteLoading = signal<boolean>(false);
  protected readonly automuteError = signal<string | null>(null);
  /** Top mute relative to the first-break picks (ms; negative = above). */
  protected readonly autoShiftMs = signal<number>(-50);
  /** Also draw the raw per-trace picks (the fit is what goes in the mute). */
  protected readonly showAutoCurves = signal<boolean>(false);
  protected readonly autoMuteIndex = AUTO_MUTE_INDEX;

  /** Pick the displayed gather's first breaks and write the straight-line fit
   * into mute 3, on |offset| as scprep does. */
  protected async autoPickMutes() {
    const loaded = this.loaded();
    const addressing = this.renderedAddressing();
    if (!loaded || !addressing) return;
    this.automuteLoading.set(true);
    this.automuteError.set(null);
    try {
      const res: SegyAutomuteResponse = await firstValueFrom(
        this.segy.getAutomute(loaded.path, loaded.isAws, {
          ...addressing,
          ...this.filterParams(),
          top_shift_ms: this.autoShiftMs(),
        }),
      );
      this.automute.set(res);
      const fit = res.top_fit;
      this.mutes.update((list) =>
        list.map((m, i) => {
          if (i !== AUTO_MUTE_INDEX || !fit) return m;
          const g = fit.growth_s_per_unit;
          return {
            ...m,
            on: true,
            start: Number(fit.start_s.toFixed(3)),
            // s per metre -> m/s; a flat fit has no growth
            growth: Math.abs(g) > 1e-9 ? Math.round(1 / g / 10) * 10 : null,
            atOffset: 0,
          };
        }),
      );
      this.muteDistance.set("offset");
      this.showMutes.set(true);
      if (!res.top_fit) {
        this.automuteError.set("No first breaks found in this gather.");
      }
    } catch (err) {
      this.automute.set(null);
      this.automuteError.set(this.errorDetail(err));
    } finally {
      this.automuteLoading.set(false);
    }
  }

  protected setMute(index: number, key: keyof SegyMute, value: any) {
    this.mutes.update((list) =>
      list.map((m, i) => (i === index ? { ...m, [key]: value } : m)),
    );
  }

  protected resetMutes() {
    this.mutes.set(DEFAULT_MUTES.map((m) => ({ ...m })));
  }

  // ── Displayed gather on the geometry map ──
  protected readonly showGatherOnMap = signal<boolean>(true);
  protected readonly boxFitAngle = signal<boolean>(false);
  protected readonly boxPadding = signal<number>(500);
  protected readonly gatherOnMap = computed<GeometryHighlight | null>(() => {
    const [sx, sy, rx, ry] = ["soux", "souy", "recx", "recy"].map((f) =>
      this.gatherColumn(f),
    );
    const traces = this.gatherHeaders()?.trace;
    if (!sx || !sy || !rx || !ry || !traces) return null;
    // match the scan's units: real units when it applied the scalars
    const scaled = this.scan()?.scaled_fields?.includes("soux") ?? this.applyScalars();
    const k = scaled ? this.coordScale() : null;
    const at = (xs: number[], ys: number[], i: number): [number, number] => [
      xs[i] * (k?.[i] ?? 1),
      ys[i] * (k?.[i] ?? 1),
    ];
    const seen = new Set<string>();
    const sources: [number, number, number][] = [];
    const receivers: [number, number, number][] = [];
    for (let i = 0; i < traces.length; i++) {
      const s = at(sx, sy, i);
      const key = `${s[0]},${s[1]}`;
      if (!seen.has(key)) {
        seen.add(key);
        sources.push([s[0], s[1], traces[i]]);
      }
      const r = at(rx, ry, i);
      receivers.push([r[0], r[1], traces[i]]);
    }
    const sel = this.selectedTraceOrdinal();
    const selected =
      sel != null && sel < traces.length
        ? { source: at(sx, sy, sel), receiver: at(rx, ry, sel), trace: traces[sel] }
        : null;
    const a = this.renderedAddressing();
    const label =
      a?.scan_field != null
        ? `${this.fieldLabel(a.scan_field)} ${a.scan_value}`
        : "displayed gather";
    return { label, sources, receivers, selected };
  });

  /** Include the header plot when screenshotting the gather. */
  protected readonly screenshotTracks = signal<boolean>(true);
  protected readonly shotMenuOpen = signal<boolean>(false);
  /** Set while capturing without the header plot. */
  protected readonly hideTracksForCapture = signal<boolean>(false);
  protected readonly displayedTracks = computed(() =>
    this.hideTracksForCapture() ? [] : this.gatherTracks(),
  );

  /** Gather + limits + filter as currently set, vs. what is on screen: the
   * View gather button flags when they differ. */
  private readonly currentParamsKey = computed(() =>
    JSON.stringify({
      ...this.gatherAddressing(),
      ...this.displayParams(),
      ...this.filterParams(),
    }),
  );
  private readonly renderedParamsKey = signal<string | null>(null);
  protected readonly needsUpdate = computed(
    () =>
      !!this.gather() &&
      this.renderedParamsKey() != null &&
      this.currentParamsKey() !== this.renderedParamsKey(),
  );

  /** The gather fills the window (Esc to leave); the spectrum is hidden. */
  protected readonly maximized = signal<boolean>(false);
  /** Spectrum pane beside the gather (collapsible to give it the width). */
  protected readonly showSpectrum = signal<boolean>(true);
  protected readonly spectrumVisible = computed(
    () => this.showSpectrum() && !this.maximized(),
  );
  private readonly gatherHeadersCard = viewChild("gatherHeadersCard", {
    read: ElementRef,
  });
  private readonly displayStage = viewChild<ElementRef<HTMLElement>>(
    "displayStage",
  );

  /** 0-based ordinal within the displayed gather (the click), or null. */
  protected readonly selectedTraceOrdinal = signal<number | null>(null);
  /** Addressing of the currently-rendered gather, snapshotted at View time so
   * its header table and trace clicks resolve against what is on screen. */
  private readonly renderedAddressing = signal<SegyGatherParams | null>(null);

  // ─────────────────────────────────────────────────────────────
  // GEOMETRY & HEADER PLOTS (one sampled header scan)
  // ─────────────────────────────────────────────────────────────

  protected readonly scan = signal<SegyScanResponse | null>(null);
  protected readonly scanLoading = signal<boolean>(false);
  protected readonly scanError = signal<string | null>(null);
  protected readonly scanPoints = signal<number>(DEFAULT_SCAN_POINTS);
  /** Return coordinates / elevations in real units (scalco / scalel). */
  protected readonly applyScalars = signal<boolean>(true);
  private lastMissingRequest: string | null = null;

  /** Sampling choices: presets, plus "every trace" when the file is small
   * enough to scan in full. */
  protected readonly scanPresets = computed(() => {
    const n = this.info()?.n_traces ?? 0;
    const presets = SCAN_PRESETS.filter((p) => !n || p < n).map((p) => ({
      value: p,
      label: `${p.toLocaleString()} traces`,
    }));
    if (n && n <= MAX_SCAN_POINTS) {
      presets.push({ value: n, label: `all ${n.toLocaleString()} traces` });
    }
    return presets;
  });

  /** The sampling shown in the picker (a request above the file's trace
   * count reads every trace). */
  protected readonly scanPointsValue = computed(() =>
    Math.min(this.scanPoints(), this.info()?.n_traces || Infinity),
  );

  protected readonly geoColor = signal<string | null>(null);
  protected readonly geoLayers = signal<GeometryLayers>({
    sources: true,
    receivers: true,
    cdps: false,
  });
  protected readonly gridBox = signal<SegyGridBox>({ ...DEFAULT_GRID_BOX });
  protected readonly xField = signal<string>("trace");
  protected readonly yField = signal<string>("ffid");
  protected readonly xplotColor = signal<string | null>(null);

  /** Fields the plots currently need that the loaded scan lacks. */
  private readonly missingScanFields = computed<string[]>(() => {
    const scan = this.scan();
    if (!scan) return [];
    return this.requestedScanFields().filter((f) => !(f in scan.fields));
  });

  /** Extra fields the scan must carry: anything a plot picks beyond the core
   * set, plus every custom field (so all pickers can offer them). */
  private readonly requestedScanFields = computed<string[]>(() => {
    const core = new Set(this.info()?.scan_fields ?? []);
    const wanted = [
      this.geoColor(),
      this.xField(),
      this.yField(),
      this.xplotColor(),
      ...this.customFieldNames(),
    ];
    return [
      ...new Set(
        wanted.filter(
          (f): f is string => !!f && f !== "trace" && !core.has(f),
        ),
      ),
    ];
  });

  // ─────────────────────────────────────────────────────────────
  // BUCKETS (top-level roots for the directory browser)
  // ─────────────────────────────────────────────────────────────
  protected readonly buckets = signal<string[]>([]);
  /** The bucket the current listing sits in (its first path segment), so the
   * bucket dropdown reflects where you are. Null outside a known bucket. */
  protected readonly currentBucket = computed<string | null>(() => {
    const first = (this.listing()?.path ?? "").split("/")[0];
    return this.buckets().includes(first) ? first : null;
  });

  // ─────────────────────────────────────────────────────────────
  // MODEL GEOMETRY (origin / bounding box / rotation of a 3D volume)
  // ─────────────────────────────────────────────────────────────
  protected readonly modelGeometry = signal<SegyModelGeometryResponse | null>(
    null,
  );
  protected readonly modelLoading = signal<boolean>(false);
  protected readonly modelError = signal<string | null>(null);
  protected readonly modelMessage = signal<string | null>(null);
  protected readonly modelMapOptions = computed<EChartsOption | null>(() =>
    this.buildModelMapOptions(),
  );

  protected readonly selectedTab = signal<number>(TAB.display);

  /** Grouping a gather by a header field needs a header scan, which is capped
   * for very large files. Above the cap grouping still works but only over the
   * leading ~5M traces; a value past that window won't be found (use the
   * trace-range mode to reach it). */
  protected readonly groupingCapped = computed<boolean>(
    () => (this.info()?.n_traces ?? 0) > 5_000_000,
  );

  // ─────────────────────────────────────────────────────────────
  // SETTINGS PERSISTENCE
  // ─────────────────────────────────────────────────────────────

  private readonly settings = computed<ViewerSettings>(() => ({
    gatherMode: this.gatherMode(),
    scanField: this.scanField(),
    traceCount: this.traceCount(),
    clipMode: this.clipMode(),
    clip: this.clip(),
    ampLimit: this.ampLimit(),
    tMin: this.tMin(),
    tMax: this.tMax(),
    lowcut: this.lowcut(),
    highcut: this.highcut(),
    lowRoll: this.lowRoll(),
    highRoll: this.highRoll(),
    lowPhase: this.lowPhase(),
    highPhase: this.highPhase(),
    spectrumLimits: this.spectrumLimits(),
    gatherColumns: this.gatherColumns(),
    tableColumns: this.tableColumns(),
    trackFields: this.trackFields(),
    showSpectrum: this.showSpectrum(),
    screenshotTracks: this.screenshotTracks(),
    showMutes: this.showMutes(),
    displayMode: this.displayMode(),
    wiggleGain: this.wiggleGain(),
    wiggleStep: this.wiggleStep(),
    mutes: this.mutes(),
    muteDistance: this.muteDistance(),
    showGatherOnMap: this.showGatherOnMap(),
    boxFitAngle: this.boxFitAngle(),
    boxPadding: this.boxPadding(),
    autoShiftMs: this.autoShiftMs(),
    showAutoPicks: this.showAutoCurves(),
    tablePlotFields: this.tablePlotFields(),
    scanPoints: this.scanPoints(),
    applyScalars: this.applyScalars(),
    geoColor: this.geoColor(),
    geoLayers: this.geoLayers(),
    xField: this.xField(),
    yField: this.yField(),
    xplotColor: this.xplotColor(),
    customFields: this.customFields(),
  }));

  private restoreSettings() {
    const s = readStorage<ViewerSettings>(SETTINGS_KEY);
    const set = <K extends keyof ViewerSettings>(
      key: K,
      target: { set(v: ViewerSettings[K]): void },
    ) => {
      if (s[key] !== undefined) target.set(s[key] as ViewerSettings[K]);
    };
    set("gatherMode", this.gatherMode);
    set("scanField", this.scanField);
    set("traceCount", this.traceCount);
    set("clipMode", this.clipMode);
    set("clip", this.clip);
    set("ampLimit", this.ampLimit);
    set("tMin", this.tMin);
    set("tMax", this.tMax);
    set("lowcut", this.lowcut);
    set("highcut", this.highcut);
    set("lowRoll", this.lowRoll);
    set("highRoll", this.highRoll);
    set("lowPhase", this.lowPhase);
    set("highPhase", this.highPhase);
    set("spectrumLimits", this.spectrumLimits);
    set("gatherColumns", this.gatherColumns);
    set("tableColumns", this.tableColumns);
    set("trackFields", this.trackFields);
    set("showSpectrum", this.showSpectrum);
    set("screenshotTracks", this.screenshotTracks);
    set("showMutes", this.showMutes);
    set("displayMode", this.displayMode);
    set("wiggleGain", this.wiggleGain);
    set("wiggleStep", this.wiggleStep);
    set("mutes", this.mutes);
    // names are positional (older saves called them outer / inner)
    this.mutes.update((list) =>
      list.map((m, i) => ({ ...m, name: `mute ${i + 1}` })),
    );
    set("muteDistance", this.muteDistance);
    set("showGatherOnMap", this.showGatherOnMap);
    set("boxFitAngle", this.boxFitAngle);
    set("boxPadding", this.boxPadding);
    set("autoShiftMs", this.autoShiftMs);
    set("showAutoPicks", this.showAutoCurves);
    set("tablePlotFields", this.tablePlotFields);
    set("scanPoints", this.scanPoints);
    set("applyScalars", this.applyScalars);
    set("geoColor", this.geoColor);
    set("geoLayers", this.geoLayers);
    set("xField", this.xField);
    set("yField", this.yField);
    set("xplotColor", this.xplotColor);
    set("customFields", this.customFields);
  }

  private readonly boxStorageKey = computed<string | null>(() => {
    const loaded = this.loaded();
    return loaded
      ? `${BOX_KEY_PREFIX}${loaded.isAws ? "s3" : "local"}:${loaded.path}`
      : null;
  });

  // ─────────────────────────────────────────────────────────────
  // DIRECTORY LOOKUP
  // ─────────────────────────────────────────────────────────────

  /** List the SEG-Y files + sub-folders of a directory. Uses the typed
   * `dirInput` unless an explicit `dir` is passed (folder navigation). */
  protected async listDir(dir?: string) {
    const path = (dir ?? this.dirInput()).trim();
    if (!path) {
      this.listError.set("Enter a directory path to list.");
      return;
    }
    this.dirInput.set(path);
    this.listLoading.set(true);
    this.listError.set(null);
    try {
      const result = await firstValueFrom(
        this.segy.listDir(path, this.isAws()),
      );
      this.listing.set(result);
    } catch (err) {
      this.listing.set(null);
      this.listError.set(this.errorDetail(err));
    } finally {
      this.listLoading.set(false);
    }
  }

  /** Drill into a sub-folder of the currently-listed directory. */
  protected openDir(name: string) {
    const base = (this.listing()?.path ?? this.dirInput()).replace(/\/+$/, "");
    this.listDir(`${base}/${name}`);
  }

  /** Go up one level from the currently-listed directory. */
  protected goUp() {
    const parent = this.parentDir();
    if (parent) this.listDir(parent);
  }

  // ── Recently opened files (this browser only) ──
  protected readonly recentFiles = signal<RecentFile[]>(readRecent());

  private rememberRecent(path: string, isAws: boolean, size: number | null) {
    // re-read so entries added from other tabs aren't dropped
    const list = [
      { path, isAws, size, openedAt: Date.now() },
      ...readRecent().filter((r) => r.path !== path || r.isAws !== isAws),
    ].slice(0, MAX_RECENT);
    writeStorage(RECENT_KEY, list);
    this.recentFiles.set(list);
  }

  protected openRecent(file: RecentFile) {
    this.isAws.set(file.isAws);
    this.pathInput.set(file.path);
    this.inputMode.set("file");
    this.load();
  }

  protected clearRecent() {
    writeStorage(RECENT_KEY, []);
    this.recentFiles.set([]);
  }

  /** Another tab opened a file: keep this tab's list in step. */
  @HostListener("window:storage", ["$event"])
  protected onStorage(event: StorageEvent) {
    if (event.key === RECENT_KEY) this.recentFiles.set(readRecent());
  }

  /** Open a listed SEG-Y file in the single-file viewer. */
  protected openFile(name: string) {
    const base = (this.listing()?.path ?? this.dirInput()).replace(/\/+$/, "");
    this.pathInput.set(`${base}/${name}`);
    this.inputMode.set("file");
    this.load();
  }

  // ─────────────────────────────────────────────────────────────
  // LOAD
  // ─────────────────────────────────────────────────────────────

  protected async load() {
    const path = this.pathInput().trim();
    if (!path) {
      this.loadError.set("Enter a path to a .sgy / .segy file.");
      return;
    }
    const isAws = this.isAws();
    this.loaded.set({ path, isAws });

    // reset everything downstream of the file selection
    this.headers.set(null);
    this.headersError.set(null);
    this.traceHeaders.set(null);
    this.traceHeadersError.set(null);
    this.tracePage.set(1);
    this.rawGather.set(null);
    this.filteredGather.set(null);
    this.clearSamples();
    this.showRaw.set(false);
    this.gatherError.set(null);
    this.spectrum.set(null);
    this.spectrumError.set(null);
    this.gatherHeaders.set(null);
    this.gatherHeadersError.set(null);
    this.selectedTraceOrdinal.set(null);
    this.renderedAddressing.set(null);
    this.maximized.set(false);
    this.automute.set(null);
    this.scan.set(null);
    this.scanError.set(null);
    this.lastMissingRequest = null;
    this.modelGeometry.set(null);
    this.modelError.set(null);
    this.modelMessage.set(null);
    this.gridBox.set({
      ...DEFAULT_GRID_BOX,
      ...readStorage<SegyGridBox>(this.boxStorageKey()!),
    });

    this.info.set(null);
    this.loadError.set(null);
    this.infoLoading.set(true);
    try {
      const info = await firstValueFrom(this.segy.getInfo(path, isAws));
      this.info.set(info);
      this.rememberRecent(path, isAws, info.size ?? null);
    } catch (err) {
      this.loadError.set(this.errorDetail(err));
      this.infoLoading.set(false);
      return; // file unreadable: don't chase headers/traces
    }
    this.infoLoading.set(false);

    this.fetchHeaders(path, isAws);
    this.fetchTraceHeaders(1);
    // re-entering a tab that lazy-loads: fetch its data for the new file
    this.onTabChange(this.selectedTab());
  }

  private async fetchHeaders(path: string, isAws: boolean) {
    this.headersLoading.set(true);
    this.headersError.set(null);
    try {
      this.headers.set(await firstValueFrom(this.segy.getHeaders(path, isAws)));
      // take the file's distance units from its binary header when it says
      this.fileUnits.set(this.headerUnits() ?? "m");
    } catch (err) {
      this.headers.set(null);
      this.headersError.set(this.errorDetail(err));
    } finally {
      this.headersLoading.set(false);
    }
  }

  /** Fetch a page of the file-wide table: every standard field + the custom
   * ones, so changing the shown columns needs no refetch. */
  private async fetchTraceHeaders(page: number) {
    const loaded = this.loaded();
    if (!loaded) return;
    const offset = (page - 1) * this.tracePageSize;
    this.traceHeadersLoading.set(true);
    this.traceHeadersError.set(null);
    try {
      this.traceHeaders.set(
        await firstValueFrom(
          this.segy.getTraceHeaders(
            loaded.path,
            loaded.isAws,
            offset,
            this.tracePageSize,
            true,
            this.customFields().map(customHeaderFieldToken),
          ),
        ),
      );
    } catch (err) {
      this.traceHeaders.set(null);
      this.traceHeadersError.set(this.errorDetail(err));
    } finally {
      this.traceHeadersLoading.set(false);
    }
  }

  protected onTraceHeaderPageChange(page: number) {
    this.tracePage.set(page);
    this.fetchTraceHeaders(page);
  }

  /** Column-chooser quick actions (shared by both header tables). */
  protected defaultColumns(): string[] {
    return [...DEFAULT_HEADER_COLUMNS];
  }
  protected allColumns(): string[] {
    return [
      ...(this.info()?.header_fields ?? []).map((f) => f.name),
      ...this.customFieldNames(),
    ];
  }

  /** Lazy-load the heavier full-file scans the first time their tab opens. */
  protected onTabChange(index: number) {
    this.selectedTab.set(index);
    if (!this.info()) return;
    if (index === TAB.geometry && !this.scan() && !this.scanLoading()) {
      this.loadScan();
    } else if (
      index === TAB.model &&
      !this.modelGeometry() &&
      !this.modelLoading()
    ) {
      this.loadModelGeometry();
    }
  }

  // ─────────────────────────────────────────────────────────────
  // CUSTOM HEADER FIELDS
  // ─────────────────────────────────────────────────────────────

  /** Add the drafted {byte, format} as a custom field, available in every
   * header picker and table. */
  protected addCustomField() {
    const byte = this.customByteDraft();
    const format = this.customFormatDraft();
    if (byte == null || byte < 1 || byte > 240) {
      this.customError.set("Byte must be between 1 and 240.");
      return;
    }
    this.customError.set(null);
    const field: CustomHeaderField = { byte, format };
    if (this.customFieldNames().includes(customHeaderFieldName(field))) return;
    this.customFields.update((list) => [...list, field]);
    this.customByteDraft.set(null);
    this.refreshForCustomFields();
  }

  protected removeCustomField(field: CustomHeaderField) {
    const name = customHeaderFieldName(field);
    this.customFields.update((list) =>
      list.filter((f) => customHeaderFieldName(f) !== name),
    );
    // fall back off a removed field everywhere it was chosen
    if (this.scanField() === name) this.scanField.set("ffid");
    if (this.geoColor() === name) this.geoColor.set(null);
    if (this.xplotColor() === name) this.xplotColor.set(null);
    if (this.xField() === name) this.xField.set("trace");
    if (this.yField() === name) this.yField.set("ffid");
    this.gatherColumns.update((c) => c.filter((x) => x !== name));
    this.tableColumns.update((c) => c.filter((x) => x !== name));
    this.trackFields.update((c) => c.filter((x) => x !== name));
    this.tablePlotFields.update((c) => c.filter((x) => x !== name));
  }

  /** New custom fields: refetch the tables that carry them (the scan picks
   * them up through `missingScanFields`). */
  private refreshForCustomFields() {
    if (this.traceHeaders()) this.fetchTraceHeaders(this.tracePage());
  }

  // ─────────────────────────────────────────────────────────────
  // DISPLAY
  // ─────────────────────────────────────────────────────────────

  /** Gather addressing from the controls. */
  private gatherAddressing(): SegyGatherParams {
    if (this.gatherMode() === "field") {
      return { scan_field: this.scanField(), scan_value: this.scanValue()! };
    }
    return {
      trace_start: this.traceStart() ?? 0,
      trace_count: this.traceCount() ?? DEFAULT_TRACE_COUNT,
    };
  }

  /** Display limits (time window + amplitude clip) from the controls. */
  private displayParams(): SegyGatherParams {
    const params: SegyGatherParams = {};
    if (this.clipMode() === "fixed" && this.ampLimit()) {
      params.min_val = -Math.abs(this.ampLimit()!);
      params.max_val = Math.abs(this.ampLimit()!);
    } else {
      params.clip = this.clip();
    }
    if (this.tMin() != null) params.t_min = this.tMin()!;
    if (this.tMax() != null) params.t_max = this.tMax()!;
    return params;
  }

  private filterParams(): SegyGatherParams {
    const params: SegyGatherParams = {};
    if (this.lowcut() != null) {
      params.lowcut = this.lowcut()!;
      params.low_roll = this.lowRoll();
      params.low_phase = this.lowPhase();
    }
    if (this.highcut() != null) {
      params.highcut = this.highcut()!;
      params.high_roll = this.highRoll();
      params.high_phase = this.highPhase();
    }
    return params;
  }

  /** Step to the previous / next gather: the adjacent header value, or the
   * adjacent block of traces. */
  protected stepGather(direction: 1 | -1) {
    if (this.gatherMode() === "field") {
      if (this.scanValue() == null) return;
      this.scanValue.set(this.scanValue()! + direction);
    } else {
      const count = this.traceCount() || DEFAULT_TRACE_COUNT;
      const next = Math.max(0, (this.traceStart() ?? 0) + direction * count);
      const n = this.info()?.n_traces ?? Infinity;
      if (next >= n) return;
      this.traceStart.set(next);
    }
    this.viewGather();
  }

  protected resetTimeWindow() {
    this.tMin.set(null);
    this.tMax.set(null);
  }

  /** Fetch the gather, its spectrum and its trace headers together (same
   * addressing, filter and time window). When a filter is active the
   * unfiltered render is fetched too, so the display can be flipped. */
  protected async viewGather() {
    const loaded = this.loaded();
    if (!loaded || !this.canViewGather()) return;
    const addressing = this.gatherAddressing();
    const display = this.displayParams();
    const filter = this.filterParams();
    const filtered = this.hasFilter();

    this.renderedAddressing.set(addressing);
    this.renderedParamsKey.set(this.currentParamsKey());
    this.automute.set(null);
    this.automuteError.set(null);
    this.selectedTraceOrdinal.set(null);
    this.gatherHeaderPage.set(1);
    this.fetchGatherHeaders();

    this.gatherLoading.set(true);
    this.gatherError.set(null);
    this.spectrumError.set(null);
    const { path, isAws } = loaded;
    this.clearSamples();
    this.renderedRequest = {
      path,
      isAws,
      params: { ...addressing, ...display },
      filter: filtered ? filter : null,
    };
    if (this.displayMode() !== "image") this.fetchSamples();
    const [raw, filt, spectrum] = await Promise.allSettled([
      firstValueFrom(
        this.segy.getGather(path, isAws, { ...addressing, ...display }),
      ),
      filtered
        ? firstValueFrom(
            this.segy.getGather(path, isAws, {
              ...addressing,
              ...display,
              ...filter,
            }),
          )
        : Promise.resolve<GatherResponse | null>(null),
      firstValueFrom(
        this.segy.getSpectrum(path, isAws, {
          ...addressing,
          ...display,
          ...filter,
        }),
      ),
    ]);

    if (raw.status === "fulfilled") {
      this.rawGather.set(raw.value);
    } else {
      this.rawGather.set(null);
      this.gatherError.set(this.gatherErrorDetail(raw.reason));
    }

    if (!filtered) {
      this.filteredGather.set(null);
    } else if (filt.status === "fulfilled") {
      this.filteredGather.set(filt.value);
    } else {
      this.filteredGather.set(null);
      if (!this.gatherError()) {
        this.gatherError.set(this.gatherErrorDetail(filt.reason));
      }
    }

    if (spectrum.status === "fulfilled") {
      this.spectrum.set(spectrum.value);
    } else {
      this.spectrum.set(null);
      this.spectrumError.set(this.errorDetail(spectrum.reason));
    }
    this.gatherLoading.set(false);
    // bring the gather + spectrum to the top of the window, at the same spot
    // every time, so browser tabs on other datasets line up for comparison
    if (!this.maximized()) {
      setTimeout(() =>
        this.displayStage()?.nativeElement.scrollIntoView({
          block: "start",
          behavior: "smooth",
        }),
      );
    }
  }

  /** Enter in any field of the settings panel (re)draws the gather. Number
   * inputs commit their value on keydown, before this bubbles up; Enter in a
   * dropdown's search box is left to pick the option. */
  protected onPanelEnter(event: Event) {
    const target = event.target as HTMLElement | null;
    if (target?.tagName !== "INPUT" || target.closest("nz-select")) return;
    if (["checkbox", "radio"].includes(target.getAttribute("type") ?? "")) return;
    event.preventDefault();
    this.viewGather();
  }

  /** Gather error detail, with a hint to fall back to trace-range addressing
   * when grouping by a header field is unavailable. */
  private gatherErrorDetail(reason: any): string {
    let detail = this.errorDetail(reason);
    if (this.gatherMode() === "field") {
      detail +=
        " If grouping by this field is unavailable, try the trace-range mode instead.";
    }
    return detail;
  }

  /** Header values of every trace of the rendered gather, for the chosen
   * columns (refetched when the columns change). */
  private async fetchGatherHeaders() {
    const loaded = this.loaded();
    const addressing = this.renderedAddressing();
    const columns = [
      ...new Set([
        ...this.gatherColumns(),
        ...this.trackFields(),
        ...GEOMETRY_FIELDS,
        "offset",
      ]),
    ];
    if (!loaded || !addressing) return;
    if (!columns.length) {
      this.gatherHeaders.set(null);
      return;
    }
    this.gatherHeadersLoading.set(true);
    this.gatherHeadersError.set(null);
    try {
      this.gatherHeaders.set(
        await firstValueFrom(
          this.segy.getGatherHeaders(
            loaded.path,
            loaded.isAws,
            addressing,
            columns,
          ),
        ),
      );
    } catch (err) {
      this.gatherHeaders.set(null);
      this.gatherHeadersError.set(this.errorDetail(err));
    } finally {
      this.gatherHeadersLoading.set(false);
    }
  }

  protected setGatherColumns(columns: string[]) {
    this.gatherColumns.set(columns);
    this.refetchGatherHeadersIfMissing();
  }

  protected setTrackFields(fields: string[]) {
    this.trackFields.set(fields.slice(0, MAX_TRACKS));
    this.refetchGatherHeadersIfMissing();
  }

  /** Only go back to the server when a newly chosen field isn't loaded. */
  private refetchGatherHeadersIfMissing() {
    const have = this.gatherHeaders()?.columns ?? [];
    const wanted = [
      ...this.gatherColumns(),
      ...this.trackFields(),
      ...GEOMETRY_FIELDS,
      "offset",
    ];
    if (wanted.some((c) => !have.includes(c))) this.fetchGatherHeaders();
  }

  protected toggleMaximized() {
    this.maximized.update((m) => !m);
  }

  @HostListener("document:keydown.escape")
  protected exitMaximized() {
    this.maximized.set(false);
  }

  /** A trace was clicked in the gather (0-based ordinal): highlight it and
   * page the header table to its row. */
  protected onTraceClick(ordinal: number) {
    this.selectedTraceOrdinal.set(ordinal);
    this.gatherHeaderPage.set(
      Math.floor(ordinal / this.gatherHeaderPageSize) + 1,
    );
    // wait for the page to render, then bring the row into view
    setTimeout(() => this.scrollToGatherRow(ordinal));
  }

  /** Scroll the gather header table's own body (not the page, which would
   * push the gather out of view) so the row sits in the middle. */
  private scrollToGatherRow(ordinal: number) {
    const card = this.gatherHeadersCard()?.nativeElement as
      | HTMLElement
      | undefined;
    const row = card?.querySelector<HTMLElement>(
      `tr[data-ordinal="${ordinal}"]`,
    );
    const body = row?.closest<HTMLElement>(".ant-table-body");
    if (!row || !body) return;
    const rowRect = row.getBoundingClientRect();
    const bodyRect = body.getBoundingClientRect();
    body.scrollTo({
      top:
        body.scrollTop +
        rowRect.top -
        bodyRect.top -
        (bodyRect.height - rowRect.height) / 2,
      behavior: "smooth",
    });
  }

  /** A row of the gather header table was clicked: mark that trace. */
  protected onGatherRowClick(ordinal: number) {
    this.selectedTraceOrdinal.set(
      this.selectedTraceOrdinal() === ordinal ? null : ordinal,
    );
  }

  // ─────────────────────────────────────────────────────────────
  // GEOMETRY & HEADER PLOTS
  // ─────────────────────────────────────────────────────────────

  /** One sampled pass over the headers for both the map and the plots. */
  protected async loadScan() {
    const loaded = this.loaded();
    if (!loaded) return;
    this.scanLoading.set(true);
    this.scanError.set(null);
    try {
      this.scan.set(
        await firstValueFrom(
          this.segy.getScan(
            loaded.path,
            loaded.isAws,
            this.scanPoints(),
            this.requestedScanFields(),
            this.applyScalars(),
          ),
        ),
      );
    } catch (err) {
      this.scanError.set(this.errorDetail(err));
    } finally {
      this.scanLoading.set(false);
    }
  }

  protected setScanPoints(points: number) {
    this.scanPoints.set(points);
    this.loadScan();
  }

  protected setApplyScalars(on: boolean) {
    this.applyScalars.set(on);
    this.loadScan();
  }

  // ─────────────────────────────────────────────────────────────
  // MODEL
  // ─────────────────────────────────────────────────────────────

  protected async loadModelGeometry() {
    const loaded = this.loaded();
    if (!loaded) return;
    this.modelLoading.set(true);
    this.modelError.set(null);
    try {
      this.modelGeometry.set(
        await firstValueFrom(
          this.segy.getModelGeometry(loaded.path, loaded.isAws),
        ),
      );
    } catch (err) {
      this.modelGeometry.set(null);
      this.modelError.set(this.errorDetail(err));
    } finally {
      this.modelLoading.set(false);
    }
  }

  /** Copy the fitted inline/crossline grid into the geometry tab's model box
   * (preprep convention: NX2 is NX1 rotated +90°, so the axes are swapped
   * when the survey grid is left-handed). */
  protected useModelGridAsBox() {
    const grid = this.modelGeometry()?.grid;
    const c = grid?.corners;
    if (!grid || !c || c.length !== 4) return;
    let a1 = [c[1][0] - c[0][0], c[1][1] - c[0][1]]; // along inlines
    let a2 = [c[3][0] - c[0][0], c[3][1] - c[0][1]]; // along crosslines
    const nIl =
      (grid.inline_range![1] - grid.inline_range![0]) /
        (grid.inline_increment || 1) +
      1;
    const nXl =
      (grid.crossline_range![1] - grid.crossline_range![0]) /
        (grid.crossline_increment || 1) +
      1;
    let [n1, n2] = [nIl, nXl];
    let [d1, d2] = [grid.bin_inline!, grid.bin_crossline!];
    if (a1[0] * a2[1] - a1[1] * a2[0] < 0) {
      [a1, a2] = [a2, a1];
      [n1, n2] = [n2, n1];
      [d1, d2] = [d2, d1];
    }
    const angle = (Math.atan2(a1[1], a1[0]) * 180) / Math.PI;
    this.gridBox.set({
      xOrigin: Number(c[0][0].toFixed(3)),
      yOrigin: Number(c[0][1].toFixed(3)),
      nx1: Math.round(n1),
      nx2: Math.round(n2),
      dx: Number(d1.toFixed(3)),
      angle: Number(angle.toFixed(3)),
      xyscale: 1,
    });
    this.modelMessage.set(
      Math.abs(d1 - d2) > 0.01 * Math.max(d1, d2)
        ? `Copied to the Geometry tab. Bins are not square (${this.formatNum(d1, 2)} × ${this.formatNum(d2, 2)}); DX uses the NX1 bin.`
        : "Copied to the Geometry tab's model box.",
    );
  }

  // ─────────────────────────────────────────────────────────────
  // BUCKETS
  // ─────────────────────────────────────────────────────────────

  private async loadBuckets() {
    try {
      const res = await firstValueFrom(this.segy.listBuckets());
      this.buckets.set(res?.buckets ?? []);
    } catch {
      this.buckets.set([]); // browsing by typed path still works
    }
  }

  /** Open a bucket as the browse root (top-level S3 listing). */
  protected browseBucket(bucket: string) {
    this.isAws.set(true);
    this.listDir(bucket);
  }

  // ─────────────────────────────────────────────────────────────
  // SCREENSHOT
  // ─────────────────────────────────────────────────────────────

  /** Capture an element to a PNG download (controls marked
   * `.omit-from-screenshot` are excluded). */
  protected async screenshot(elementId: string, section: string) {
    const el = document.getElementById(elementId);
    if (!el) return;
    try {
      const dataUrl = await toPng(el, {
        pixelRatio: window.devicePixelRatio || 2,
        backgroundColor: "#ffffff",
        filter: (node: any) =>
          !node?.classList?.contains?.("omit-from-screenshot"),
      });
      const link = document.createElement("a");
      link.download = `${this.screenshotStem(section)}.png`;
      link.href = dataUrl;
      link.click();
    } catch {
      // capture failures (e.g. tainted canvas) are non-fatal
    }
  }

  /** Screenshot from the gather toolbar menu; drops the header plot for the
   * capture when it is switched off. */
  protected async screenshotDisplay(elementId: string, section: string) {
    this.shotMenuOpen.set(false);
    const dropTracks =
      !this.screenshotTracks() && this.gatherTracks().length > 0;
    if (dropTracks) {
      this.hideTracksForCapture.set(true);
      // let the viewer re-measure and redraw without the tracks
      await new Promise((r) => setTimeout(r, 120));
    }
    try {
      await this.screenshot(elementId, section);
    } finally {
      if (dropTracks) this.hideTracksForCapture.set(false);
    }
  }

  private screenshotStem(section: string): string {
    const base = this.fileName(this.loaded()?.path ?? "segy");
    return `${base}-${section}`.replace(/[^A-Za-z0-9._-]+/g, "_");
  }

  // ─────────────────────────────────────────────────────────────
  // CHART OPTIONS
  // ─────────────────────────────────────────────────────────────

  /** Map of the model footprint: the rotated grid corners (affine fit) or the
   * axis-aligned bounding box, with the origin marked. */
  private buildModelMapOptions(): EChartsOption | null {
    const model = this.modelGeometry();
    if (!model || !model.bbox) return null;
    const bbox = model.bbox;
    const grid = model.grid;

    const corners: number[][] =
      grid?.corners && grid.corners.length === 4
        ? grid.corners.map((c) => [c[0], c[1]])
        : [
            [bbox.min_x, bbox.min_y],
            [bbox.max_x, bbox.min_y],
            [bbox.max_x, bbox.max_y],
            [bbox.min_x, bbox.max_y],
          ];
    const ring = [...corners, corners[0]];
    const series: any[] = [
      {
        name: "Footprint",
        type: "line",
        data: ring,
        lineStyle: { color: "#1890ff", width: 2 },
        areaStyle: { color: "rgba(24,144,255,0.08)" },
        showSymbol: false,
        animation: false,
        z: 1,
      },
    ];
    if (grid?.origin) {
      series.push({
        name: "Origin",
        type: "scatter",
        data: [grid.origin],
        symbolSize: 10,
        itemStyle: { color: "#ff4d4f" },
        animation: false,
        z: 2,
      });
    }

    const xs = ring.map((p) => p[0]);
    const ys = ring.map((p) => p[1]);
    const xMin = Math.min(...xs);
    const xMax = Math.max(...xs);
    const yMin = Math.min(...ys);
    const yMax = Math.max(...ys);
    const span = Math.max(xMax - xMin, yMax - yMin) || 1;
    const half = (span * 1.1) / 2;
    const xMid = (xMin + xMax) / 2;
    const yMid = (yMin + yMax) / 2;

    return {
      animation: false,
      legend: { top: 0, data: series.map((s) => s.name) },
      grid: { left: 10, right: 30, top: 30, bottom: 45, containLabel: true },
      tooltip: {
        trigger: "item",
        formatter: (p: any) => {
          const [x, y] = p.value ?? [];
          return `${p.seriesName}<br/>x: ${Number(x).toFixed(1)}<br/>y: ${Number(
            y,
          ).toFixed(1)}`;
        },
      },
      xAxis: {
        type: "value",
        name: "X",
        nameLocation: "middle",
        nameGap: 25,
        min: Math.round(xMid - half),
        max: Math.round(xMid + half),
      },
      yAxis: {
        type: "value",
        name: "Y",
        nameLocation: "middle",
        nameGap: 55,
        min: Math.round(yMid - half),
        max: Math.round(yMid + half),
      },
      series,
    } as EChartsOption;
  }

  // ─────────────────────────────────────────────────────────────
  // DISPLAY HELPERS
  // ─────────────────────────────────────────────────────────────

  protected readonly byteLabel = byteRangeLabel;

  protected fieldLabel(name: string): string {
    return this.fieldLabels().get(name) ?? name;
  }

  /** Table column headers: label + byte range. */
  private columnInfo(columns: string[]) {
    const bytes = this.fieldBytes();
    return columns.map((name) => ({
      name,
      label: this.fieldLabel(name),
      bytes: bytes[name] ?? null,
    }));
  }

  /** Compact coordinate / number formatting for the readouts. */
  protected formatNum(value: number | null | undefined, digits = 1): string {
    if (value == null || !isFinite(value)) return "—";
    return Number(value).toLocaleString(undefined, {
      maximumFractionDigits: digits,
    });
  }

  protected fileName(path: string): string {
    return path?.split("/").pop() || path;
  }

  protected formatBytes(size: number | null | undefined): string {
    if (size == null) return "—";
    if (size < 1024) return `${size} B`;
    const units = ["KB", "MB", "GB", "TB"];
    let value = size;
    let unit = "B";
    for (const next of units) {
      if (value < 1024) break;
      value /= 1024;
      unit = next;
    }
    return `${value.toFixed(value < 10 ? 1 : 0)} ${unit}`;
  }

  /** Sample interval for display; values < 1 are taken to be seconds, larger
   * ones microseconds (the SEG-Y binary-header convention). */
  protected formatDt(dt: number | null | undefined): string {
    if (dt == null) return "—";
    const ms = dt < 1 ? dt * 1000 : dt / 1000;
    return `${Number(ms.toFixed(3))} ms`;
  }

  protected formatLabel(code: number | null | undefined): string {
    if (code == null) return "format —";
    return `${FORMAT_LABELS[code] ?? "unknown format"} (${code})`;
  }

  protected errorDetail(err: any): string {
    return (
      err?.error?.detail ?? err?.detail ?? err?.message ?? "Request failed."
    );
  }
}
